'use client';

import Image from 'next/image';
import { Check as CheckIcon, ChevronDownIcon, Loader2Icon, Package } from 'lucide-react';
import { isPregeneratedThumbnailUrl, useImageVariant } from '@/lib/image-variant';
import i18nextMip from '@/lib/i18n';

const tMip = (k: string, opts?: any): string => String(i18nextMip.t(k, opts));
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { FM_CONTEXT_MENU_SURFACE } from '@/components/ui/menu-surface';

export type UseMultiImagePickerParams = {
  urls: string[];
  activeUrl?: string;
  /**
   * 缩略图的默认宽高比（在图片尚未加载到自然宽高前用于骨架占位，减少布局抖动）。
   * 例如：'16/9'、'1024/768'
   */
  defaultThumbAspectRatio?: string;
  /**
   * 缩略图显示高度（px）。组件会保持原比例，不做裁切。
   */
  thumbHeightPx: number;
  onSelectUrl: (url: string) => void;
  disabled?: boolean;
};

export type MultiImagePickerState = ReturnType<typeof useMultiImagePicker>;

export function useMultiImagePicker(params: UseMultiImagePickerParams) {
  const { urls, activeUrl, defaultThumbAspectRatio, thumbHeightPx, onSelectUrl, disabled } = params;

  const safeUrls = useMemo(() => (Array.isArray(urls) ? urls.filter(Boolean) : []), [urls]);

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  // Per-url fallbacks / metadata
  const [thumbAspect, setThumbAspect] = useState<Record<string, string>>({});
  const [thumbLoaded, setThumbLoaded] = useState<Record<string, true>>({});
  const [selectedMap, setSelectedMap] = useState<Record<string, true>>({});

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Auto-close when single image.
  useEffect(() => {
    if (safeUrls.length <= 1) setOpen(false);
  }, [safeUrls.length]);

  // Click outside / Esc to close.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: Event) => {
      try {
        const target = e.target as Node | null;
        if (!target) return;
        // Radix ContextMenu content is portaled to <body>.
        // Clicking menu items should NOT be treated as "click outside" for this panel,
        // otherwise the panel will close immediately and the menu feels unfocusable.
        if (
          target instanceof HTMLElement &&
          (target.closest('[data-slot="context-menu-content"]') ||
            target.closest('[data-slot="context-menu-sub-content"]'))
        ) {
          return;
        }
        if (triggerRef.current && triggerRef.current.contains(target)) return;
        if (menuRef.current && menuRef.current.contains(target)) return;
        setOpen(false);
      } catch {}
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Request a slightly higher-res image than display size (avoid blur).
  const requestPx = useMemo(() => {
    const v = Math.round(Number(thumbHeightPx) * 2);
    return Math.max(160, Number.isFinite(v) ? v : 160);
  }, [thumbHeightPx]);

  // 缩略图规格：回退（缩略图 → 原图 → 占位图标）由各项的 useImageVariant 处理
  const thumbTransform = useMemo(
    () => ({ width: requestPx, height: requestPx, resize: 'contain', quality: 75 }) as const,
    [requestPx],
  );

  const onThumbLoaded = (url: string, img: HTMLImageElement) => {
    try {
      setThumbLoaded((prev) => (prev[url] ? prev : { ...prev, [url]: true }));
      const w = img?.naturalWidth;
      const h = img?.naturalHeight;
      if (!Number.isFinite(w) || !Number.isFinite(h) || (w as number) <= 0 || (h as number) <= 0) return;
      const next = `${w}/${h}`;
      setThumbAspect((prev) => (prev[url] === next ? prev : { ...prev, [url]: next }));
    } catch {}
  };

  const getThumbStyle = (url: string) => {
    const ar = thumbAspect[url] || defaultThumbAspectRatio || '1/1';
    return { aspectRatio: ar, height: `${thumbHeightPx}px` } as const;
  };

  const isActive = (url: string) => Boolean(activeUrl && url === activeUrl);

  const isSelected = (url: string) => Boolean(selectedMap[url]);
  const getSelectedUrls = useCallback(() => {
    try {
      return Object.keys(selectedMap).filter((u) => selectedMap[u]);
    } catch {
      return [];
    }
  }, [selectedMap]);
  const clearSelected = useCallback(() => setSelectedMap({}), []);
  const setSelectedOnly = useCallback((url: string) => {
    const u = String(url || '').trim();
    if (!u) return;
    setSelectedMap({ [u]: true });
  }, []);
  const toggleSelected = useCallback((url: string) => {
    const u = String(url || '').trim();
    if (!u) return;
    setSelectedMap((prev) => {
      if (prev[u]) {
        const next = { ...prev };
        delete next[u];
        return next;
      }
      return { ...prev, [u]: true };
    });
  }, []);

  const selectUrl = (url: string) => {
    if (disabled) return;
    onSelectUrl(url);
  };

  return {
    urls: safeUrls,
    activeUrl,
    defaultThumbAspectRatio,
    thumbHeightPx,
    open,
    setOpen,
    focused,
    setFocused,
    disabled: Boolean(disabled),
    triggerRef,
    menuRef,
    isActive,
    isSelected,
    getSelectedUrls,
    clearSelected,
    setSelectedOnly,
    toggleSelected,
    isThumbLoaded: (url: string) => Boolean(thumbLoaded[url]),
    selectUrl,
    thumbTransform,
    onThumbLoaded,
    getThumbStyle,
  };
}

/** 单个缩略图：缩略图 → 原图 → 占位图标；加载完成前显示骨架 */
function PickerThumb({ state, url, index }: { state: MultiImagePickerState; url: string; index: number }) {
  const { src, onError, exhausted } = useImageVariant(
    url.startsWith('Asset://') ? undefined : url,
    state.thumbTransform,
  );
  if (!src || exhausted) {
    return (
      <div className="absolute inset-0 z-10 flex items-center justify-center bg-primary/5">
        <Package size={16} className="text-primary/60" />
      </div>
    );
  }
  return (
    <>
      {!state.isThumbLoaded(url) ? (
        <>
          <div className="pointer-events-none absolute inset-0 z-0 rounded-xl bg-white/5 animate-pulse" />
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <Loader2Icon className="h-4 w-4 text-white/60 animate-spin" />
          </div>
        </>
      ) : null}
      <Image
        src={src}
        // 预生成缩略图直接加载（已足够小）；其余仍经 Next 图片优化
        unoptimized={isPregeneratedThumbnailUrl(src)}
        alt={`Image ${index + 1}`}
        fill
        sizes={`${state.thumbHeightPx}px`}
        className="object-contain relative z-10"
        loading="lazy"
        onLoadingComplete={(img) => state.onThumbLoaded(url, img)}
        onError={onError}
      />
    </>
  );
}

export function MultiImageTrigger(props: {
  state: MultiImagePickerState;
  ariaLabel?: string;
  widthPx?: number;
  className?: string;
  /**
   * 点击触发器时额外执行的逻辑（例如选中节点）。
   * 会在切换 open 之前调用。
   */
  onTriggerClick?: () => void;
}) {
  const { state, ariaLabel, widthPx = 100, className, onTriggerClick } = props;
  if (state.urls.length <= 1) return null;

  return (
    <button
      ref={state.triggerRef}
      type="button"
      className={
        `absolute top-3 right-3 z-20 inline-flex items-center justify-center gap-2 aspect-[16/9] px-3 rounded-2xl border border-white/10 bg-zinc-950/25 backdrop-blur-xl text-[14px] font-semibold tracking-tight text-white/90 shadow-xl shadow-black/20 hover:bg-zinc-950/30 active:scale-[0.98] transition ` +
        `${state.disabled ? 'opacity-50 pointer-events-none' : ''} ` +
        `${className ?? ''}`
      }
      style={{ width: `${widthPx}px` }}
      aria-label={ariaLabel ?? 'Images'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          onTriggerClick?.();
        } catch {}
        state.setOpen(!state.open);
      }}
      onFocus={() => state.setFocused(true)}
      onBlur={() => state.setFocused(false)}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <span>+{Math.max(1, state.urls.length - 1)}</span>
      <ChevronDownIcon
        className={`h-5 w-5 opacity-80 transition-transform duration-200 ${
          (state.focused || state.open) ? '-rotate-90' : 'rotate-0'
        }`}
      />
    </button>
  );
}

export function MultiImageMenuPanel(props: {
  state: MultiImagePickerState;
  /**
   * 右键菜单：“新建节点”
   */
  onCreateNodeFromThumb?: (url: string, index: number) => void;
  /**
   * 批量新建：若存在多选，则会优先走这里一次性创建多个节点。
   */
  onCreateNodesFromThumbs?: (urls: string[]) => void;
  createNodeLabel?: string;
  className?: string;
}) {
  const { state, className, onCreateNodeFromThumb, onCreateNodesFromThumbs, createNodeLabel } = props;
  if (!state.open || state.urls.length <= 1) return null;

  return (
    <div
      ref={state.menuRef}
      className={
        `absolute right-0 top-1/2 -translate-y-1/2 translate-x-[calc(100%+0.75rem)] z-40 grid w-max grid-cols-[repeat(4,max-content)] gap-2 ` +
        `${className ?? ''}`
      }
    >
      {state.urls.map((u, i) => {
        const active = state.isActive(u);
        const selected = state.isSelected(u);
        const style = state.getThumbStyle(u);
        return (
          <ContextMenu key={`${u}-${i}`}>
            <ContextMenuTrigger
              asChild
            >
              <button
                type="button"
                className="relative overflow-hidden rounded-xl bg-transparent focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/60 focus-visible:outline-offset-[-2px]"
                style={style}
                title={`Image ${i + 1}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Ctrl / Cmd 多选：只切换“选择集”，不改当前主图
                  const multi = Boolean((e as any)?.ctrlKey || (e as any)?.metaKey);
                  if (multi) {
                    state.toggleSelected(u);
                    return;
                  }
                  // 普通点击：切换主图，并变为单选
                  state.selectUrl(u);
                  state.setSelectedOnly(u);
                }}
                onContextMenu={(e) => {
                  // 只阻止冒泡到画布根层的 ContextMenuTrigger；
                  // 不要用 capture 阶段拦截，否则可能影响 Radix 打开菜单。
                  e.stopPropagation();
                  // 右键时：若该项不在当前选择中，则将其设为唯一选择（菜单默认作用于“当前项”）
                  try {
                    if (!state.isSelected(u)) state.setSelectedOnly(u);
                  } catch {}
                }}
                onMouseDown={(e) => {
                  // 允许右键触发 Radix ContextMenu；左键/中键仍拦截以避免画布拖拽等副作用
                  if ((e as any)?.button === 2) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  if ((e as any)?.button === 2) return;
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <PickerThumb state={state} url={u} index={i} />
                {/* 右上角 1/4 圆角标 */}
                <div className="pointer-events-none absolute top-0 right-0 z-20 h-7 w-7 bg-black/35 text-white text-[11px] font-semibold backdrop-blur rounded-bl-full flex items-center justify-center leading-none">
                  {i + 1}
                </div>
                {/* 多选勾选标记：用于区分“主图显示(active)”与“多选集合(selected)” */}
                {selected ? (
                  <div className="pointer-events-none absolute bottom-1 left-1 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-sm">
                    <CheckIcon className="h-3.5 w-3.5" />
                  </div>
                ) : null}
                {/* 选中框：在顶层（覆盖图片与角标） */}
                {active ? (
                  <div className="pointer-events-none absolute inset-0 z-30 rounded-xl ring-2 ring-inset ring-primary" />
                ) : null}
              </button>
            </ContextMenuTrigger>

            {(onCreateNodeFromThumb || onCreateNodesFromThumbs) ? (
              <ContextMenuContent
                className={FM_CONTEXT_MENU_SURFACE}
                // 阻止在菜单上继续冒泡到画布根层（避免同时弹出画布菜单）
                onContextMenu={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <ContextMenuItem
                  onSelect={() => {
                    const selected = state.getSelectedUrls();
                    const urls =
                      Array.isArray(selected) && selected.length > 0
                        ? selected
                        : [u];
                    if (urls.length > 1 && onCreateNodesFromThumbs) {
                      onCreateNodesFromThumbs(urls);
                    } else {
                      // fallback: single create (or batch without dedicated handler)
                      if (!onCreateNodeFromThumb) return;
                      urls.forEach((url, idx) => onCreateNodeFromThumb(url, idx));
                    }
                    // 执行后主动收起缩略图面板，避免 UI 悬空
                    state.setOpen(false);
                  }}
                >
                  {(() => {
                    const count = state.getSelectedUrls().length;
                    if (count > 1) return tMip('新建 {{count}} 个节点', { count });
                    return createNodeLabel ?? tMip('新建节点');
                  })()}
                </ContextMenuItem>
              </ContextMenuContent>
            ) : null}
          </ContextMenu>
        );
      })}
    </div>
  );
}

